import React, { useState, useEffect } from 'react';
import { FaDownload, FaCalendarCheck, FaArrowRight } from 'react-icons/fa';
import fullLogo from '../assets/CollectedAF_Logo.png';
import { ReactComponent as LocationArrow } from '../assets/location-arrow.svg';
import type { WebAppConfig } from '../services/webAppConfig';

const SASH_BLUE = '#0066cc';

const APP_WORDS = [
  'Fearless Social', 'Fast Discovery', 'Frictionless', 'Free-Flowing',
  'Fully Live', 'Flash Moments', 'First-Move Friendly', 'Face-to-Face Ready',
  'Fast-Moving', 'Far-Reaching', 'Forward-Focused', 'Fluid Connections',
  'Always-On Flow', 'Feel-Good', 'Fresh Connections', 'Fun-First',
  'Fired-Up', 'Fear-of-Missing-Nothing', 'Findable', 'Fun',
];

interface LeftSectionProps {
  onDownloadClick: () => void;
  onBookDemoClick: () => void;
  onExperienceClick: () => void;
  onPrivacyPolicyClick: () => void;
  isDownloadActive: boolean;
  isBookDemoActive: boolean;
  videoUrl?: string;
  loading?: boolean;
  config?: WebAppConfig;
}

const LeftSection: React.FC<LeftSectionProps> = ({ onDownloadClick, onBookDemoClick, onExperienceClick, onPrivacyPolicyClick, isDownloadActive, isBookDemoActive, videoUrl, loading, config }) => {
  const [phase, setPhase] = useState<'intro' | 'done'>('intro');
  const [appWordIndex, setAppWordIndex] = useState(0);

  useEffect(() => {
    const wordDurations = [
      80, 80, 90, 90, 100, 110, 120, 140,
      170, 210, 260, 320,
      400, 520, 650, 800,
      1000, 1400, 1800, 3500,
    ];
    const totalIntroDuration = wordDurations.reduce((a, b) => a + b, 0);
    const wordTimers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < APP_WORDS.length; i++) {
      const delay = wordDurations.slice(0, i).reduce((a, b) => a + b, 0);
      wordTimers.push(setTimeout(() => setAppWordIndex(i), delay));
    }
    const doneTimer = setTimeout(() => setPhase('done'), totalIntroDuration);
    return () => { wordTimers.forEach(clearTimeout); clearTimeout(doneTimer); };
  }, []);

  const hero = config?.hero ?? {
    leftTitle: 'Collected',
    leftSubtitle: 'AF',
    tagline1: 'Every person is an opportunity — and the world is yours to collect.',
    tagline2: 'The first proximity-powered platform that connects people and venues in real time.',
  };
  const ctas = config?.ctas ?? {
    download: { text: 'Download App', visible: true },
    bookDemo: { text: 'Book a Demo', visible: true },
  };

  const glassCard = 'bg-WHITE/[0.07] backdrop-blur-2xl border border-WHITE/[0.12] shadow-[0_8px_32px_rgba(0,0,0,0.3)]';

  return (
    <div className="w-full lg:w-[20%] z-20 animate-fade-in flex-shrink-0 flex flex-col justify-start pt-24 lg:pt-8">

      {/* ── Brand hero card ── */}
      <button
        type="button"
        onClick={onExperienceClick}
        className={`relative w-full text-left rounded-2xl p-5 lg:p-6 mb-5 cursor-pointer transition-all duration-300 hover:border-WHITE/25 hover:shadow-[0_8px_40px_rgba(0,0,0,0.4)] group ${glassCard}`}
      >
        <div className="flex flex-col items-start min-w-0 w-full overflow-hidden">
          <div className="font-black leading-none mb-1.5 flex flex-wrap" style={{ fontSize: 'clamp(1.25rem, 2.5vw, 2.25rem)' }}>
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #000000, #0066cc)' }}
            >
              {hero.leftTitle}
            </span>
            <span
              className={`drop-shadow-[0_0_12px_rgba(255,255,255,0.2)] ${phase === 'done' ? 'text-WHITE' : ''}`}
              style={phase === 'intro' ? { color: SASH_BLUE } : undefined}
            >
              {phase === 'intro' ? 'ACTIVE' : hero.leftSubtitle}
            </span>
          </div>

          <div className="flex items-center gap-2.5 mt-1.5">
            <div className="h-px w-8 bg-gradient-to-r from-WHITE/50 to-transparent" />
            <span className="text-WHITE/80 text-lg lg:text-xl font-bold tracking-wider">
              {phase === 'intro' ? APP_WORDS[appWordIndex] : 'App'}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-2 text-WHITE/50 text-xs font-medium tracking-wide">
            <LocationArrow style={{ color: SASH_BLUE }} width={16} height={16} aria-hidden className="flex-shrink-0" />
            <span><span className="text-WHITE/70">a</span>ctive <span className="text-WHITE/70">f</span>un</span>
          </div>
        </div>
        <FaArrowRight className="absolute top-5 right-5 text-WHITE/20 group-hover:text-WHITE/50 transition-colors" size={14} />
      </button>

      {/* ── Taglines ── */}
      <div
        className="group relative rounded-2xl overflow-hidden mb-5 transition-all duration-500"
        style={{ perspective: '600px' }}
      >
        {/* Animated border */}
        <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #0ea5e9, #ffffff, #3b82f6)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
          <div className="w-full h-full rounded-2xl bg-BLACK/80" />
        </div>
        {/* Glow */}
        <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-700 pointer-events-none" style={{ background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #0ea5e9, #3b82f6)', filter: 'blur(16px)' }} />

        <div
          className="relative rounded-2xl p-5 backdrop-blur-2xl bg-BLACK/50 transition-transform duration-500"
          style={{ transformStyle: 'preserve-3d' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(-2deg) rotateY(2deg) scale(1.01)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0) rotateY(0) scale(1)'}
        >
          {/* Tagline 1 */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-WHITE/10 border border-WHITE/20 flex items-center justify-center mt-0.5">
              <span className="text-WHITE text-xs font-bold">✦</span>
            </div>
            <p className="text-WHITE/95 text-sm font-semibold leading-relaxed">
              {hero.tagline1}
            </p>
          </div>

          {/* Divider with glow */}
          <div className="relative h-px w-full mb-3">
            <div className="absolute inset-0 bg-WHITE/10" />
            <div className="absolute inset-y-0 left-1/4 right-1/4 bg-gradient-to-r from-transparent via-BLUE_AF/40 to-transparent" />
          </div>

          {/* Tagline 2 */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-WHITE/10 border border-WHITE/20 flex items-center justify-center mt-0.5">
              <span className="text-WHITE text-xs font-bold">⚡</span>
            </div>
            <p className="text-WHITE/70 text-xs leading-relaxed">
              {hero.tagline2}
            </p>
          </div>
        </div>
      </div>

      {/* ── CTA buttons ── */}
      <div className="flex flex-col gap-4 mb-5" style={{ perspective: '800px' }}>
        {ctas.download.visible && (
          <button
            onClick={onDownloadClick}
            className="group relative rounded-2xl overflow-hidden transition-all duration-500"
            style={{ transformStyle: 'preserve-3d' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(-4deg) rotateY(3deg) scale(1.03)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0) rotateY(0) scale(1)'}
          >
            {/* Animated gradient border */}
            <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 0deg, #3b82f6, #0ea5e9, #ffffff, #3b82f6)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
              <div className="w-full h-full rounded-2xl bg-BLACK/80" />
            </div>
            {/* Shimmer sweep */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.12) 50%, transparent 70%)', backgroundSize: '200% 100%', animation: 'none' }}>
            </div>
            {/* Content */}
            <div className={`relative flex items-center justify-center gap-3 px-5 py-4 rounded-2xl text-sm font-bold transition-all duration-300 ${
              isDownloadActive
                ? 'bg-WHITE/95 text-BLACK shadow-[0_0_30px_rgba(255,255,255,0.3)]'
                : 'bg-BLACK/60 backdrop-blur-2xl text-WHITE'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-[-8deg] ${
                isDownloadActive ? 'bg-BLACK/10' : 'bg-WHITE/10 group-hover:bg-WHITE/20'
              }`}>
                <FaDownload size={14} className={`transition-transform duration-500 group-hover:translate-y-[2px] ${isDownloadActive ? 'text-BLACK' : 'text-green-300'}`} />
              </div>
              <span className={`tracking-wide ${isDownloadActive ? '' : 'text-green-400'}`}>{ctas.download.text}</span>
              <FaArrowRight size={12} className={`transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 ${isDownloadActive ? 'text-BLACK' : 'text-WHITE/70'}`} />
            </div>
          </button>
        )}
        {ctas.bookDemo.visible && (
          <button
            onClick={onBookDemoClick}
            className="group relative rounded-2xl overflow-hidden transition-all duration-500"
            style={{ transformStyle: 'preserve-3d' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(4deg) rotateY(-3deg) scale(1.03)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0) rotateY(0) scale(1)'}
          >
            {/* Animated gradient border */}
            <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 180deg, #f97316, #fbbf24, #ffffff, #f97316)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
              <div className="w-full h-full rounded-2xl bg-BLACK/80" />
            </div>
            {/* Shimmer sweep */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.12) 50%, transparent 70%)', backgroundSize: '200% 100%' }}>
            </div>
            {/* Content */}
            <div className={`relative flex items-center justify-center gap-3 px-5 py-4 rounded-2xl text-sm font-bold transition-all duration-300 ${
              isBookDemoActive
                ? 'bg-WHITE/95 text-BLACK shadow-[0_0_30px_rgba(255,255,255,0.3)]'
                : 'bg-BLACK/60 backdrop-blur-2xl text-WHITE'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-[8deg] ${
                isBookDemoActive ? 'bg-BLACK/10' : 'bg-WHITE/10 group-hover:bg-WHITE/20'
              }`}>
                <FaCalendarCheck size={14} className={`transition-transform duration-500 group-hover:scale-110 ${isBookDemoActive ? 'text-BLACK' : 'text-blue-300'}`} />
              </div>
              <span className={`tracking-wide ${isBookDemoActive ? '' : 'text-blue-400'}`}>{ctas.bookDemo.text}</span>
              <FaArrowRight size={12} className={`transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 ${isBookDemoActive ? 'text-BLACK' : 'text-WHITE/70'}`} />
            </div>
          </button>
        )}
      </div>

      {/* ── Mobile video ── */}
      <div className="lg:hidden mb-5 w-full">
        <div className={`rounded-2xl overflow-hidden ${glassCard}`}>
          {loading ? (
            <div className="w-full h-72 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-WHITE/30 border-t-WHITE"></div>
            </div>
          ) : videoUrl ? (
            <video src={videoUrl} controls loop muted playsInline autoPlay className="w-full h-72 object-cover" />
          ) : (
            <div className="w-full h-72 flex items-center justify-center">
              <span className="text-WHITE/40 text-sm">Loading video...</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile quick-nav pills ── */}
      <div className="lg:hidden flex flex-wrap items-center gap-2 mb-6">
        {config?.sections?.showHome && (
          <button
            type="button"
            onClick={onExperienceClick}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${glassCard} text-WHITE/80 hover:text-WHITE hover:bg-WHITE/[0.12]`}
          >
            Home
          </button>
        )}
        {config?.sections?.showPrivacyPolicy && (
          <button
            type="button"
            onClick={onPrivacyPolicyClick}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${glassCard} text-WHITE/80 hover:text-WHITE hover:bg-WHITE/[0.12]`}
          >
            Privacy
          </button>
        )}
        {/* Active state dots */}
        <div className="flex items-center gap-1.5 ml-auto">
          {ctas.download.visible && (
            <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${isDownloadActive ? 'bg-WHITE scale-125' : 'bg-WHITE/30'}`} />
          )}
          {ctas.bookDemo.visible && (
            <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${isBookDemoActive ? 'bg-WHITE scale-125' : 'bg-WHITE/30'}`} />
          )}
        </div>
      </div>

      {/* ── Desktop logo ── */}
      <div className="hidden lg:flex lg:justify-center mt-auto">
        <img src={fullLogo} alt="CollectedAF Logo" className="w-80 h-auto drop-shadow-2xl opacity-90" />
      </div>
    </div>
  );
};

export default LeftSection;
